import { useEffect, useMemo, useState } from 'react';
import { useRecorder } from './hooks/useRecorder';
import { api, ApiError, type Section, type Specialty } from './api/client';
import { TelegramLogin } from './auth/TelegramLogin';
import {
  clearStoredAuth,
  getStoredName,
  getStoredToken,
} from './auth/telegramBootstrap';
import { Dashboard } from './Dashboard';

const SPECIALTY_KEY = 'med_specialty';
const BOT_DM_OK_KEY = 'med_bot_dm_confirmed';
const MODE_KEY = 'med_mode'; // 'sections' | 'whole'

type Status = 'idle' | 'recording' | 'transcribing' | 'structuring' | 'done' | 'error';

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME as string | undefined;
const BOT_LINK = BOT_USERNAME ? `https://t.me/${BOT_USERNAME}` : undefined;

// Fallback на случай если /sections недоступен (бэк не успел подняться).
const FALLBACK_SECTIONS: Section[] = [
  { slug: 'complaints', name: 'Жалобы' },
  { slug: 'history', name: 'Анамнез' },
  { slug: 'examination', name: 'Осмотр' },
  { slug: 'diagnosis', name: 'Диагноз' },
  { slug: 'recommendations', name: 'Рекомендации' },
];

interface SectionState {
  transcript: string;
  edited: string;
  structured: string;
  status: Status;
  error: string | null;
  errorKind: 'error' | 'warning';
}

function emptySection(): SectionState {
  return {
    transcript: '',
    edited: '',
    structured: '',
    status: 'idle',
    error: null,
    errorKind: 'error',
  };
}

export default function App() {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [userName, setUserName] = useState<string | null>(() => getStoredName());
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState<'work' | 'dashboard'>('work');

  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    api
      .me()
      .then((me) => {
        setUserName(me.name);
        setIsAdmin(me.is_admin);
      })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, [token]);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Загрузка…</div>
      </div>
    );
  }

  if (!token) {
    return <LoginScreen />;
  }

  if (view === 'dashboard' && isAdmin) {
    return <Dashboard onClose={() => setView('work')} />;
  }

  return (
    <WorkScreen
      userName={userName}
      isAdmin={isAdmin}
      onOpenDashboard={() => setView('dashboard')}
      onLogout={() => {
        clearStoredAuth();
        setToken(null);
        setUserName(null);
      }}
    />
  );
}

function Spinner() {
  return (
    <svg
      className="animate-spin h-5 w-5 text-gray-700"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      />
    </svg>
  );
}

function LoginScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 space-y-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Medical Assistant</h1>
        <p className="text-sm text-gray-600">
          Войдите через Telegram. Доступ открыт только заранее одобренным сотрудникам.
        </p>
        {BOT_USERNAME ? (
          <div className="flex justify-center">
            <TelegramLogin botUsername={BOT_USERNAME} />
          </div>
        ) : (
          <div className="text-sm text-red-600">
            VITE_BOT_USERNAME не задан. Заполни frontend/.env.
          </div>
        )}
        <p className="text-xs text-gray-400">
          Если бота нет в кабинете Telegram — попросите Славу добавить ваш ID
          в whitelist.
        </p>
      </div>
    </div>
  );
}

function WorkScreen({
  userName,
  isAdmin,
  onOpenDashboard,
  onLogout,
}: {
  userName: string | null;
  isAdmin: boolean;
  onOpenDashboard: () => void;
  onLogout: () => void;
}) {
  const {
    isRecording,
    isPaused,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    error: recorderError,
    volume,
    waveformHistory,
    silenceSeconds,
  } = useRecorder();

  // Список секций + активная.
  const [sections, setSections] = useState<Section[]>(FALLBACK_SECTIONS);
  const [activeSection, setActiveSection] = useState<string>('complaints');

  // Состояния по секциям. Ключи — slug. Лениво инициализируем для всех.
  const [sectionStates, setSectionStates] = useState<Record<string, SectionState>>(
    () =>
      Object.fromEntries(FALLBACK_SECTIONS.map((s) => [s.slug, emptySection()])),
  );

  const [lastBlob, setLastBlob] = useState<Blob | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [specialty, setSpecialty] = useState<string>(
    () => localStorage.getItem(SPECIALTY_KEY) || '',
  );

  const [authorCanReceive, setAuthorCanReceive] = useState<boolean | null>(() => {
    return localStorage.getItem(BOT_DM_OK_KEY) === '1' ? true : null;
  });

  // Режим: 'sections' = таб на каждую секцию (как раньше)
  //        'whole'    = одна запись на весь приём, AI разносит
  const [mode, setMode] = useState<'sections' | 'whole'>(
    () => (localStorage.getItem(MODE_KEY) === 'whole' ? 'whole' : 'sections'),
  );
  const setModeAndPersist = (m: 'sections' | 'whole') => {
    setMode(m);
    localStorage.setItem(MODE_KEY, m);
  };

  // ─── Состояние для режима «Весь приём» ────────────────────────────
  // Один общий транскрипт (без привязки к секции).
  const [wholeStatus, setWholeStatus] = useState<Status>('idle');
  const [wholeTranscript, setWholeTranscript] = useState('');
  const [wholeEdited, setWholeEdited] = useState('');
  const [wholeError, setWholeError] = useState<string | null>(null);
  const [wholeErrorKind, setWholeErrorKind] = useState<'error' | 'warning'>('error');
  const [splitting, setSplitting] = useState(false);

  const current = sectionStates[activeSection] ?? emptySection();

  // helper для частичного обновления состояния активной секции
  const updateActive = (patch: Partial<SectionState>) => {
    setSectionStates((prev) => ({
      ...prev,
      [activeSection]: { ...(prev[activeSection] ?? emptySection()), ...patch },
    }));
  };

  // Таймер записи. Пауза не тикает; при resume берём отсчёт с сохранённой
  // отметки, поэтому счётчик продолжается с того же места, где остановились.
  useEffect(() => {
    if (!isRecording) {
      setRecordSeconds(0);
      return;
    }
    if (isPaused) {
      return; // на паузе счётчик замирает
    }
    // Смещаем виртуальный start так, чтобы Date.now() - start == recordSeconds*1000
    const start = Date.now() - recordSeconds * 1000;
    const id = setInterval(() => {
      setRecordSeconds(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, isPaused]);

  // /specialties + /sections при загрузке.
  useEffect(() => {
    api
      .specialties()
      .then((r) => {
        setSpecialties(r.specialties);
        if (!specialty) {
          setSpecialty(r.default);
          localStorage.setItem(SPECIALTY_KEY, r.default);
        }
      })
      .catch(() => {});
    api
      .sections()
      .then((r) => {
        setSections(r.sections);
        // Обеспечиваем состояния для всех секций (если бэк прислал
        // больше чем в FALLBACK).
        setSectionStates((prev) => {
          const next: Record<string, SectionState> = { ...prev };
          for (const s of r.sections) {
            if (!next[s.slug]) next[s.slug] = emptySection();
          }
          return next;
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSpecialtyChange = (slug: string) => {
    setSpecialty(slug);
    localStorage.setItem(SPECIALTY_KEY, slug);
  };

  const showToast = (msg: string, ms = 2500) => {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  const tryAutoCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('✓ Скопировано в буфер');
    } catch {}
  };

  const handleStart = async () => {
    updateActive({
      error: null,
      errorKind: 'error',
      transcript: '',
      edited: '',
      structured: '',
    });
    setLastBlob(null);
    try {
      await startRecording();
      updateActive({ status: 'recording' });
    } catch (e) {
      updateActive({
        error: e instanceof Error ? e.message : 'Ошибка записи',
        status: 'error',
      });
    }
  };

  const processBlob = async (blob: Blob, autostopReason?: string) => {
    setLastBlob(blob);
    updateActive({
      status: 'transcribing',
      error: autostopReason ?? null,
      errorKind: autostopReason ? 'warning' : 'error',
    });

    try {
      const { text } = await api.transcribe(blob, specialty);

      if (!text.trim()) {
        updateActive({
          transcript: '',
          edited: '',
          error: 'Не удалось распознать речь. Попробуйте ещё раз.',
          errorKind: 'error',
          status: 'error',
        });
        return;
      }

      updateActive({
        transcript: text,
        edited: text,
        status: 'structuring',
      });

      const resp = await api.structure(text, specialty, activeSection);
      setAuthorCanReceive(resp.author_can_receive);
      if (resp.author_can_receive) localStorage.setItem(BOT_DM_OK_KEY, '1');

      updateActive({
        structured: resp.structured,
        status: 'done',
      });
      void tryAutoCopy(resp.structured);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка обработки';
      updateActive({ error: msg, errorKind: 'error', status: 'error' });
    }
  };

  const handleStop = async () => {
    updateActive({ status: 'transcribing' });
    const blob = await stopRecording();
    if (!blob) {
      updateActive({ status: 'idle' });
      return;
    }
    await processBlob(blob);
  };

  // Раньше здесь был onAutostop callback: если вкладка ушла в фон, запись
  // автоматически стопилась и уходила на обработку. Теперь вкладка → пауза,
  // а не стоп — врач сам решает, продолжить или завершить (см. useRecorder).

  const handleRestructure = async () => {
    if (!current.edited.trim()) return;
    updateActive({ status: 'structuring', error: null });
    try {
      const resp = await api.structure(current.edited, specialty, activeSection);
      setAuthorCanReceive(resp.author_can_receive);
      if (resp.author_can_receive) localStorage.setItem(BOT_DM_OK_KEY, '1');
      updateActive({ structured: resp.structured, status: 'done' });
      void tryAutoCopy(resp.structured);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка обработки';
      updateActive({ error: msg, errorKind: 'error', status: 'error' });
    }
  };

  // ─── Handlers режима «Весь приём» ────────────────────────────────
  const handleWholeStart = async () => {
    setWholeError(null);
    setWholeErrorKind('error');
    setWholeTranscript('');
    setWholeEdited('');
    setLastBlob(null);
    try {
      await startRecording();
      setWholeStatus('recording');
    } catch (e) {
      setWholeError(e instanceof Error ? e.message : 'Ошибка записи');
      setWholeStatus('error');
    }
  };

  const handleWholeStop = async () => {
    setWholeStatus('transcribing');
    const blob = await stopRecording();
    if (!blob) {
      setWholeStatus('idle');
      return;
    }
    setLastBlob(blob);
    await processWholeBlob(blob);
  };

  const processWholeBlob = async (blob: Blob, autostopReason?: string) => {
    setWholeStatus('transcribing');
    if (autostopReason) {
      setWholeError(autostopReason);
      setWholeErrorKind('warning');
    }
    try {
      const { text } = await api.transcribe(blob, specialty);
      if (!text.trim()) {
        setWholeError('Не удалось распознать речь. Попробуйте ещё раз.');
        setWholeErrorKind('error');
        setWholeStatus('error');
        return;
      }
      setWholeTranscript(text);
      setWholeEdited(text);
      setWholeStatus('done');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка обработки';
      setWholeError(msg);
      setWholeErrorKind('error');
      setWholeStatus('error');
    }
  };

  const handleSplit = async () => {
    if (!wholeEdited.trim()) return;
    setSplitting(true);
    setWholeError(null);
    try {
      const r = await api.structureProtocol(wholeEdited, specialty);
      setAuthorCanReceive(r.author_can_receive);
      if (r.author_can_receive) localStorage.setItem(BOT_DM_OK_KEY, '1');

      // Раскидываем по табам и переключаемся на первую непустую.
      setSectionStates((prev) => {
        const next = { ...prev };
        const fields: Record<string, string> = {
          complaints: r.complaints,
          history: r.history,
          examination: r.examination,
          diagnosis: r.diagnosis,
          recommendations: r.recommendations,
        };
        for (const [slug, structured] of Object.entries(fields)) {
          next[slug] = {
            ...(prev[slug] ?? emptySection()),
            structured: structured.trim(),
            // Транскрипт ассоциируем с целым приёмом — кладём в обе поля
            // только для секции «complaints», чтобы юзер мог его править
            // и перегенерировать через обычный structure если надо.
            // Остальные секции — только structured, без transcript.
            transcript: slug === 'complaints' ? wholeEdited : '',
            edited: slug === 'complaints' ? wholeEdited : '',
            status: structured.trim() ? 'done' : 'idle',
            error: null,
            errorKind: 'error',
          };
        }
        return next;
      });

      // Переключаем на первую непустую.
      const firstFilled = ['complaints', 'history', 'examination', 'diagnosis', 'recommendations']
        .find((s) => {
          const v = (
            s === 'complaints' ? r.complaints :
            s === 'history' ? r.history :
            s === 'examination' ? r.examination :
            s === 'diagnosis' ? r.diagnosis :
            r.recommendations
          );
          return v.trim();
        });
      if (firstFilled) setActiveSection(firstFilled);

      showToast('✓ Разложено по секциям');
      // После успешного split — переключаемся на режим секций.
      setModeAndPersist('sections');
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось разложить';
      setWholeError(msg);
      setWholeErrorKind('error');
    } finally {
      setSplitting(false);
    }
  };

  const handleDownloadAudio = () => {
    if (!lastBlob) return;
    const url = URL.createObjectURL(lastBlob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const ext = lastBlob.type.includes('mp4')
      ? 'm4a'
      : lastBlob.type.includes('ogg')
      ? 'ogg'
      : 'webm';
    a.download = `medical-${activeSection}-${stamp}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    if (!current.structured) return;
    try {
      await navigator.clipboard.writeText(current.structured);
      showToast('✓ Скопировано');
    } catch {
      updateActive({
        error: 'Не удалось скопировать. Скопируйте вручную.',
        errorKind: 'error',
      });
    }
  };

  const handleResetActive = () => {
    updateActive({
      transcript: '',
      edited: '',
      structured: '',
      error: null,
      status: 'idle',
    });
  };

  const handleDismissError = () => {
    updateActive({
      error: null,
      status: current.status === 'error' ? 'idle' : current.status,
    });
  };

  // «Скопировать весь протокол» — собирает все заполненные секции.
  const fullProtocol = useMemo(() => {
    const parts: string[] = [];
    for (const sec of sections) {
      const st = sectionStates[sec.slug];
      if (st && st.structured.trim()) {
        parts.push(st.structured.trim());
      }
    }
    return parts.join('\n\n');
  }, [sections, sectionStates]);

  const handleCopyAll = async () => {
    if (!fullProtocol) return;
    try {
      await navigator.clipboard.writeText(fullProtocol);
      showToast('✓ Полный протокол скопирован');
    } catch {
      showToast('Не удалось скопировать — скопируй вручную');
    }
  };

  const handleDownloadPdf = async (variant: 'full' | 'patient') => {
    const payload = {
      complaints: sectionStates['complaints']?.structured || '',
      history: sectionStates['history']?.structured || '',
      examination: sectionStates['examination']?.structured || '',
      diagnosis: sectionStates['diagnosis']?.structured || '',
      recommendations: sectionStates['recommendations']?.structured || '',
      specialty,
    };
    try {
      const blob = await api.downloadPdf(variant, payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = variant === 'patient'
        ? `memo-${stamp}.pdf`
        : `protocol-${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(variant === 'patient' ? '✓ Памятка скачана' : '✓ Протокол скачан');
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Не удалось скачать PDF');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8 px-3 sm:px-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 0.75rem)' }}>
      <div className="max-w-2xl mx-auto space-y-3 sm:space-y-4">
        {/* Header: название + видимый селект специальности справа.
            На мобильном селект селект уезжает на отдельную строку — palец легче. */}
        <header className="sticky top-0 bg-gray-50/95 backdrop-blur z-10 py-2 -mx-3 sm:-mx-4 px-3 sm:px-4 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Медицинский протокол
              </h1>
              {userName && (
                <p className="text-xs sm:text-sm text-gray-500 truncate">{userName}</p>
              )}
            </div>
            {specialties.length > 0 && (
              <select
                value={specialty}
                onChange={(e) => handleSpecialtyChange(e.target.value)}
                className="flex-shrink-0 border border-gray-300 rounded-lg px-3 py-2 text-base bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] max-w-[55%]"
                aria-label="Специальность"
              >
                {specialties.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        {authorCanReceive !== true && BOT_LINK && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
            <div className="flex-1 text-blue-900">
              📬 Получай отчёты сессий в Telegram — подключи бота.
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a
                href={BOT_LINK}
                target="_blank"
                rel="noreferrer"
                className="flex-1 sm:flex-none px-4 py-2 bg-blue-600 active:bg-blue-800 hover:bg-blue-700 text-white rounded-lg text-sm font-medium text-center min-h-[40px] flex items-center justify-center"
              >
                Открыть бот
              </a>
              <button
                onClick={() => {
                  localStorage.setItem(BOT_DM_OK_KEY, '1');
                  setAuthorCanReceive(true);
                }}
                className="flex-1 sm:flex-none px-4 py-2 bg-white active:bg-gray-100 hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium min-h-[40px]"
              >
                Я подключил(а)
              </button>
            </div>
          </div>
        )}

        {/* Переключатель режима записи */}
        <div className="flex bg-gray-100 p-1 rounded-xl text-base">
          <button
            onClick={() => setModeAndPersist('sections')}
            disabled={isRecording || wholeStatus === 'recording'}
            className={
              'flex-1 py-3 rounded-lg font-medium transition-colors min-h-[44px] ' +
              (mode === 'sections'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 active:text-gray-900 disabled:opacity-50')
            }
          >
            🎯 По секциям
          </button>
          <button
            onClick={() => setModeAndPersist('whole')}
            disabled={isRecording || wholeStatus === 'recording'}
            className={
              'flex-1 py-3 rounded-lg font-medium transition-colors min-h-[44px] ' +
              (mode === 'whole'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 active:text-gray-900 disabled:opacity-50')
            }
          >
            📑 Весь приём
          </button>
        </div>

        {mode === 'whole' && (
          <WholeRecordingBlock
            status={wholeStatus}
            transcript={wholeTranscript}
            edited={wholeEdited}
            onEditedChange={setWholeEdited}
            error={wholeError ?? recorderError}
            errorKind={wholeErrorKind}
            onDismissError={() => {
              setWholeError(null);
              if (wholeStatus === 'error') setWholeStatus('idle');
            }}
            volume={volume}
            waveformHistory={waveformHistory}
            silenceSeconds={silenceSeconds}
            recordSeconds={recordSeconds}
            splitting={splitting}
            lastBlob={lastBlob}
            isPaused={isPaused}
            onStart={handleWholeStart}
            onStop={handleWholeStop}
            onPause={pauseRecording}
            onResume={resumeRecording}
            onSplit={handleSplit}
            onDownloadAudio={handleDownloadAudio}
          />
        )}

        {mode === 'sections' && (<><div className="relative -mx-3 sm:-mx-4">
          {/* gradient hint что есть скролл вправо */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none z-10 sm:hidden" aria-hidden="true" />
          <div className="flex gap-2 overflow-x-auto px-3 sm:px-4 pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {sections.map((s) => {
            const isActive = s.slug === activeSection;
            const hasResult = !!sectionStates[s.slug]?.structured.trim();
            const inProgress =
              !!sectionStates[s.slug] &&
              ['recording', 'transcribing', 'structuring'].includes(
                sectionStates[s.slug].status,
              );
            return (
              <button
                key={s.slug}
                onClick={() => setActiveSection(s.slug)}
                disabled={isRecording && !isActive}
                className={
                  'flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-1.5 min-h-[40px] ' +
                  (isActive
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed')
                }
              >
                <span>{s.name}</span>
                {hasResult && <span className={isActive ? 'text-green-300' : 'text-green-600'}>●</span>}
                {inProgress && !hasResult && <Spinner />}
              </button>
            );
          })}
          </div>
        </div>

        {/* Кнопка записи / остановки / прогресс */}
        {current.status === 'idle' || current.status === 'error' ? (
          <button
            onClick={handleStart}
            className="w-full py-6 sm:py-7 bg-red-500 active:bg-red-700 hover:bg-red-600 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg transition-colors min-h-[64px]"
          >
            ● Записать {sectionNameOf(sections, activeSection).toLowerCase()}
          </button>
        ) : current.status === 'recording' && isPaused ? (
          <div className="space-y-2">
            <button
              onClick={resumeRecording}
              className="w-full py-6 sm:py-7 bg-green-600 active:bg-green-800 hover:bg-green-700 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg flex items-center justify-center gap-3 min-h-[64px]"
            >
              <span className="inline-block w-3 h-3 bg-white rounded-full" />
              <span>Продолжить</span>
              <span className="font-mono text-2xl tabular-nums opacity-90">
                {Math.floor(recordSeconds / 60).toString().padStart(2, '0')}:
                {(recordSeconds % 60).toString().padStart(2, '0')}
              </span>
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleStop}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-xl"
              >
                Завершить и обработать
              </button>
            </div>
            <div className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2 px-3">
              ⏸ Запись на паузе — вкладка была свёрнута или экран блокировался.
            </div>
          </div>
        ) : current.status === 'recording' ? (
          <div className="space-y-2">
            <button
              onClick={handleStop}
              className="relative w-full py-6 sm:py-7 bg-gray-700 active:bg-gray-900 hover:bg-gray-800 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg flex items-center justify-center gap-3 sm:gap-4 overflow-hidden min-h-[64px]"
            >
              <span
                className="absolute inset-0 bg-red-500/40 transition-transform duration-100 ease-out origin-left"
                style={{ transform: `scaleX(${Math.max(0.02, volume).toFixed(3)})` }}
                aria-hidden="true"
              />
              <span className="relative flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span>Остановить</span>
              </span>
              <span className="relative font-mono text-2xl tabular-nums">
                {Math.floor(recordSeconds / 60).toString().padStart(2, '0')}:
                {(recordSeconds % 60).toString().padStart(2, '0')}
              </span>
            </button>
            <button
              onClick={pauseRecording}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              ⏸ Пауза
            </button>
          </div>
        ) : (
          <button
            disabled
            className="w-full py-6 sm:py-7 bg-gray-300 text-gray-700 text-lg sm:text-xl rounded-2xl flex items-center justify-center gap-3 min-h-[64px]"
          >
            <Spinner />
            <span>
              {current.status === 'transcribing' ? 'Распознавание речи…' : 'Структурирование…'}
            </span>
          </button>
        )}

        {current.status === 'recording' && (
          <>
            <Waveform history={waveformHistory} />
            <SilenceBanner seconds={silenceSeconds} />
          </>
        )}

        {(current.error || recorderError) && (
          <div
            className={
              current.errorKind === 'warning'
                ? 'bg-amber-50 border-2 border-amber-200 text-amber-900 p-4 rounded-xl relative'
                : 'bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl relative'
            }
          >
            <button
              onClick={handleDismissError}
              className="absolute top-2 right-3 text-xl leading-none opacity-60 hover:opacity-100"
              aria-label="Закрыть"
            >
              ×
            </button>
            <div className="font-semibold mb-1 pr-6">
              {current.errorKind === 'warning' ? 'Внимание' : 'Ошибка'}
            </div>
            <div className="text-sm pr-6">{current.error || recorderError}</div>
            {lastBlob && (
              <button
                onClick={handleDownloadAudio}
                className={
                  current.errorKind === 'warning'
                    ? 'mt-3 text-sm underline text-amber-800 hover:text-amber-950'
                    : 'mt-3 text-sm underline text-red-700 hover:text-red-900'
                }
              >
                💾 Скачать аудио — на случай если запись понадобится
              </button>
            )}
          </div>
        )}

        {current.transcript && (
          <div className="bg-white border border-gray-200 p-4 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase text-gray-500 font-semibold">
                Распознано · {sectionNameOf(sections, activeSection)}
              </div>
              <div className="text-xs text-gray-400">
                {current.edited.length} символов · отредактируй если ASR ошибся
              </div>
            </div>
            <textarea
              value={current.edited}
              onChange={(e) => updateActive({ edited: e.target.value })}
              rows={Math.min(8, Math.max(2, Math.ceil(current.edited.length / 80)))}
              className="w-full text-base text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              spellCheck
            />
            {current.edited.trim() !== current.transcript.trim() && current.status === 'done' && (
              <button
                onClick={handleRestructure}
                className="w-full py-2.5 bg-blue-50 active:bg-blue-200 hover:bg-blue-100 text-blue-700 font-medium text-sm rounded-lg border border-blue-200 transition-colors min-h-[44px]"
              >
                ↻ Структурировать заново (текст изменён)
              </button>
            )}
          </div>
        )}

        {current.structured && (
          <div className="bg-white border-2 border-gray-300 p-5 rounded-xl">
            <div className="text-xs uppercase text-gray-500 font-semibold mb-3">
              Готово · {sectionNameOf(sections, activeSection)}
            </div>
            <pre className="whitespace-pre-wrap font-mono text-base text-gray-900 leading-relaxed">
              {current.structured}
            </pre>
            <div className="mt-5 flex gap-3">
              <button
                onClick={handleCopy}
                className="flex-1 py-3 bg-blue-600 active:bg-blue-800 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors min-h-[48px] text-base"
              >
                📋 Скопировать секцию
              </button>
              <button
                onClick={handleResetActive}
                className="px-5 py-3 bg-gray-200 active:bg-gray-400 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors min-h-[48px] text-base"
              >
                Заново
              </button>
            </div>
            {authorCanReceive === false && BOT_LINK && (
              <div className="mt-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
                <div className="font-semibold mb-1">⚠️ Отчёт не пришёл в твой Telegram</div>
                <div>
                  Бот не может писать тебе, пока ты не нажмёшь у него Start.{' '}
                  <a href={BOT_LINK} target="_blank" rel="noreferrer" className="underline font-medium">
                    Открыть @{BOT_USERNAME}
                  </a>{' '}
                  → Start. Следующая сессия придёт в TG.
                </div>
              </div>
            )}
          </div>
        )}
        </>)}

        {/* «Скопировать весь протокол» — виден если хотя бы одна секция заполнена */}
        {fullProtocol && (
          <div className="sticky bottom-3 bg-gray-900 text-white rounded-xl p-4 shadow-lg z-10 space-y-3">
            <div className="text-sm min-w-0">
              <div className="font-semibold">Протокол готов</div>
              <div className="text-gray-300 text-xs">
                Секций: {sections.filter((s) => sectionStates[s.slug]?.structured.trim()).length} из {sections.length}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCopyAll}
                className="flex-1 min-w-[140px] px-3 py-3 bg-white text-gray-900 active:bg-gray-200 hover:bg-gray-100 rounded-lg font-semibold text-sm transition-colors min-h-[44px]"
              >
                📋 Скопировать
              </button>
              <button
                onClick={() => handleDownloadPdf('full')}
                className="flex-1 min-w-[120px] px-3 py-3 bg-gray-700 text-white active:bg-gray-600 hover:bg-gray-800 rounded-lg font-semibold text-sm transition-colors min-h-[44px]"
              >
                📄 PDF протокол
              </button>
              <button
                onClick={() => handleDownloadPdf('patient')}
                className="flex-1 min-w-[120px] px-3 py-3 bg-gray-700 text-white active:bg-gray-600 hover:bg-gray-800 rounded-lg font-semibold text-sm transition-colors min-h-[44px]"
              >
                👤 Памятка пациенту
              </button>
            </div>
          </div>
        )}

        <footer className="text-xs text-gray-400 text-center pt-2 space-y-2">
          <div>Аудио удаляется после обработки. Текст не сохраняется на сервере.</div>
          <div className="flex justify-center gap-4">
            {isAdmin && (
              <button onClick={onOpenDashboard} className="underline hover:text-gray-700">
                📊 Статистика
              </button>
            )}
            <a href="/apk/" className="underline hover:text-gray-700">
              Скачать приложение
            </a>
            <button onClick={onLogout} className="underline hover:text-gray-700">
              Выйти
            </button>
          </div>
        </footer>

        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

function sectionNameOf(sections: Section[], slug: string): string {
  return sections.find((s) => s.slug === slug)?.name ?? slug;
}

function WholeRecordingBlock({
  status,
  transcript,
  edited,
  onEditedChange,
  error,
  errorKind,
  onDismissError,
  volume,
  waveformHistory,
  silenceSeconds,
  recordSeconds,
  splitting,
  lastBlob,
  isPaused,
  onStart,
  onStop,
  onPause,
  onResume,
  onSplit,
  onDownloadAudio,
}: {
  status: Status;
  transcript: string;
  edited: string;
  onEditedChange: (v: string) => void;
  error: string | null;
  errorKind: 'error' | 'warning';
  onDismissError: () => void;
  volume: number;
  waveformHistory: number[];
  silenceSeconds: number;
  recordSeconds: number;
  splitting: boolean;
  lastBlob: Blob | null;
  isPaused: boolean;
  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onSplit: () => void;
  onDownloadAudio: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-700">
        <div className="font-semibold mb-1">📑 Режим «Весь приём»</div>
        <div className="text-gray-600">
          Включи запись в начале приёма, говори в естественном порядке (диалог
          с пациентом, осмотр, диагноз и т.д.). После остановки AI сам разложит
          всё по 5 секциям.
        </div>
        <div className="text-amber-700 text-xs mt-2">
          ⏱ Оптимально 5–20 минут. Дольше 40 минут — может не загрузиться (лимит размера).
        </div>
      </div>

      {status === 'idle' || status === 'error' ? (
        <button
          onClick={onStart}
          className="w-full py-6 sm:py-7 bg-red-500 active:bg-red-700 hover:bg-red-600 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg transition-colors min-h-[64px]"
        >
          ● Начать запись приёма
        </button>
      ) : status === 'recording' && isPaused ? (
        <div className="space-y-2">
          <button
            onClick={onResume}
            className="w-full py-6 sm:py-7 bg-green-600 active:bg-green-800 hover:bg-green-700 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg flex items-center justify-center gap-3 min-h-[64px]"
          >
            <span className="inline-block w-3 h-3 bg-white rounded-full" />
            <span>Продолжить</span>
            <span className="font-mono text-2xl tabular-nums opacity-90">
              {Math.floor(recordSeconds / 60).toString().padStart(2, '0')}:
              {(recordSeconds % 60).toString().padStart(2, '0')}
            </span>
          </button>
          <button
            onClick={onStop}
            className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-xl"
          >
            Завершить и обработать
          </button>
          <div className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg py-2 px-3">
            ⏸ Запись на паузе — вкладка была свёрнута или экран блокировался.
          </div>
        </div>
      ) : status === 'recording' ? (
        <div className="space-y-2">
          <button
            onClick={onStop}
            className="relative w-full py-6 sm:py-7 bg-gray-700 active:bg-gray-900 hover:bg-gray-800 text-white text-lg sm:text-xl font-semibold rounded-2xl shadow-lg flex items-center justify-center gap-3 sm:gap-4 overflow-hidden min-h-[64px]"
          >
            <span
              className="absolute inset-0 bg-red-500/40 transition-transform duration-100 ease-out origin-left"
              style={{ transform: `scaleX(${Math.max(0.02, volume).toFixed(3)})` }}
              aria-hidden="true"
            />
            <span className="relative flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span>Остановить</span>
            </span>
            <span className="relative font-mono text-2xl tabular-nums">
              {Math.floor(recordSeconds / 60).toString().padStart(2, '0')}:
              {(recordSeconds % 60).toString().padStart(2, '0')}
            </span>
          </button>
          <button
            onClick={onPause}
            className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            ⏸ Пауза
          </button>
        </div>
      ) : (
        <button
          disabled
          className="w-full py-6 sm:py-7 bg-gray-300 text-gray-700 text-lg sm:text-xl rounded-2xl flex items-center justify-center gap-3 min-h-[64px]"
        >
          <Spinner />
          <span>Распознавание речи…</span>
        </button>
      )}

      {status === 'recording' && (
        <>
          <Waveform history={waveformHistory} />
          <SilenceBanner seconds={silenceSeconds} />
        </>
      )}

      {error && (
        <div
          className={
            errorKind === 'warning'
              ? 'bg-amber-50 border-2 border-amber-200 text-amber-900 p-4 rounded-xl relative'
              : 'bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl relative'
          }
        >
          <button
            onClick={onDismissError}
            className="absolute top-2 right-3 text-xl leading-none opacity-60 hover:opacity-100"
            aria-label="Закрыть"
          >
            ×
          </button>
          <div className="font-semibold mb-1 pr-6">
            {errorKind === 'warning' ? 'Внимание' : 'Ошибка'}
          </div>
          <div className="text-sm pr-6">{error}</div>
          {lastBlob && (
            <button
              onClick={onDownloadAudio}
              className={
                errorKind === 'warning'
                  ? 'mt-3 text-sm underline text-amber-800 hover:text-amber-950'
                  : 'mt-3 text-sm underline text-red-700 hover:text-red-900'
              }
            >
              💾 Скачать аудио — на случай если запись понадобится
            </button>
          )}
        </div>
      )}

      {transcript && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase text-gray-500 font-semibold">
              Распознано (весь приём)
            </div>
            <div className="text-xs text-gray-400">
              {edited.length} симв. · отредактируй если ASR ошибся
            </div>
          </div>
          <textarea
            value={edited}
            onChange={(e) => onEditedChange(e.target.value)}
            rows={Math.min(14, Math.max(4, Math.ceil(edited.length / 80)))}
            className="w-full text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            spellCheck
          />
          <button
            onClick={onSplit}
            disabled={splitting || !edited.trim()}
            className="w-full py-3 bg-blue-600 active:bg-blue-800 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 min-h-[48px] text-base"
          >
            {splitting ? <><Spinner /><span>Разносим по секциям…</span></> : <span>↶ Разложить на 5 секций</span>}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Waveform-визуализация. Показываем последние ~2 сек истории громкости
 * как bar-chart, слева — прошлое, справа — «сейчас». Нужен чтобы
 * врач визуально видел, что микрофон реально записывает.
 *
 * Reason: Кирилл записал полный приём на Mac, а до бэкенда дошло только
 * 15-20 минут (браузер оборвал MediaRecorder тихо). Живая волна = не тихо.
 */
function Waveform({ history }: { history: number[] }) {
  return (
    <div
      className="flex items-end justify-between gap-[2px] h-16 px-3 py-2 bg-white border border-gray-200 rounded-xl"
      aria-label="Индикатор уровня микрофона"
    >
      {history.map((v, i) => {
        // Логарифмическая шкала — тихая речь тоже видна как заметный bar.
        const h = Math.max(4, Math.min(100, Math.round(Math.sqrt(v) * 100)));
        // Свежие sample'ы (справа) чуть ярче — приятный эффект «свежести».
        const opacity = 0.4 + (i / history.length) * 0.6;
        return (
          <div
            key={i}
            className="flex-1 bg-red-500 rounded-sm"
            style={{ height: `${h}%`, opacity }}
          />
        );
      })}
    </div>
  );
}

/**
 * Баннер «микрофон не слышит вас» при затяжной тишине.
 * Порог 15 сек подобран так, чтобы обычные паузы врача между фразами
 * не триггерили, а реально долгое молчание (потерян микрофон,
 * системное mute, экран уснул) — было видно.
 */
function SilenceBanner({ seconds }: { seconds: number }) {
  if (seconds < 15) return null;
  const secondsRounded = Math.floor(seconds);
  return (
    <div className="bg-amber-50 border-2 border-amber-200 text-amber-900 p-3 rounded-xl text-sm">
      <div className="font-semibold mb-1">
        ⚠ Микрофон молчит ~{secondsRounded} сек
      </div>
      <div className="text-amber-800">
        Проверь, что микрофон включён и не приглушён системой. Если запись
        идёт, но ничего не слышно — лучше остановить и начать заново.
      </div>
    </div>
  );
}
